import { SelectedFileStatus } from '@vizit/atoms';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { UploadQueue } from '../upload-queue';
import {
  type UploadFunction,
  UploadQueueContext,
  type UploadQueueContextType,
  type UploadQueueFileInput,
  type UploadQueueItemData,
} from './upload-queue.context';
import { uploadQueueReducer } from './upload-queue.state';

const AUTO_REMOVE_DELAY_MS = 8000;
const MAX_QUEUE_SIZE = 50;

interface UploadQueueProviderProps {
  /**
   * Child components
   * @type {React.ReactNode}
   */
  children?: React.ReactNode;
  /**
   * Maximum number of concurrent uploads
   * @type {number}
   * @default 5
   * @optional
   */
  maxConcurrent?: number;
}

/**
 * Provider component for managing upload queue state
 */
export function UploadQueueProvider(props: UploadQueueProviderProps) {
  const { children, maxConcurrent = 5 } = props;

  const [state, dispatch] = useReducer(uploadQueueReducer, { items: [] });

  const activeUploadsRef = useRef<Set<string>>(new Set());
  const autoRemoveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const uploadFunctionsRef = useRef<Map<string, UploadFunction>>(new Map());

  const processQueue = useCallback(() => {
    const readyItems = state.items.filter(
      (item) => item.status === SelectedFileStatus.Ready && !activeUploadsRef.current.has(item.id),
    );

    const availableSlots = maxConcurrent - activeUploadsRef.current.size;
    const itemsToStart = readyItems.slice(0, availableSlots);

    if (itemsToStart.length === 0) return;

    // Mark items as uploading
    dispatch({ type: 'SET_UPLOADING', payload: { ids: itemsToStart.map((i) => i.id) } });

    for (const item of itemsToStart) {
      activeUploadsRef.current.add(item.id);
      const uploadFn = uploadFunctionsRef.current.get(item.id);

      // Start upload asynchronously
      (async () => {
        try {
          if (!uploadFn) {
            throw new Error('Upload function not found for item');
          }

          await uploadFn((progress) => {
            dispatch({ type: 'UPDATE_PROGRESS', payload: { id: item.id, progress } });
          });
          dispatch({ type: 'UPDATE_STATUS', payload: { id: item.id, status: SelectedFileStatus.Success } });

          // Auto-remove after delay
          const timer = setTimeout(() => {
            dispatch({ type: 'REMOVE_ITEM', payload: { id: item.id } });
            autoRemoveTimersRef.current.delete(item.id);
            uploadFunctionsRef.current.delete(item.id);
          }, AUTO_REMOVE_DELAY_MS);
          autoRemoveTimersRef.current.set(item.id, timer);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Upload failed';
          dispatch({
            type: 'UPDATE_STATUS',
            payload: { id: item.id, status: SelectedFileStatus.Error, error: errorMessage },
          });
        } finally {
          activeUploadsRef.current.delete(item.id);
        }
      })();
    }
  }, [state.items, maxConcurrent]);

  const handleOnAddFiles = useCallback(
    (files: UploadQueueFileInput[]): void => {
      const availableSlots = MAX_QUEUE_SIZE - state.items.length;

      if (availableSlots <= 0) {
        throw new Error(`Queue is full. Maximum ${MAX_QUEUE_SIZE} items allowed.`);
      }

      if (files.length > availableSlots) {
        throw new Error(
          `Cannot add ${files.length} files. Only ${availableSlots} slot${availableSlots !== 1 ? 's' : ''} available in queue.`,
        );
      }

      const newItems: UploadQueueItemData[] = files.map((input) => {
        const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        // Store upload function in ref (can't store functions in reducer state)
        uploadFunctionsRef.current.set(id, input.onUpload);
        return {
          id,
          file: input.file,
          progress: 0,
          status: SelectedFileStatus.Ready,
          onUpload: input.onUpload,
        };
      });

      dispatch({ type: 'ADD_FILES', payload: newItems });
    },
    [state.items.length],
  );

  const handleOnRemoveItem = useCallback((id: string) => {
    // Clear any pending auto-remove timer
    const timer = autoRemoveTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      autoRemoveTimersRef.current.delete(id);
    }

    dispatch({ type: 'REMOVE_ITEM', payload: { id } });
    activeUploadsRef.current.delete(id);
    uploadFunctionsRef.current.delete(id);
  }, []);

  const handleOnClearCompleted = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' });
  }, []);

  const isUploading = useMemo(
    () => state.items.some((item) => item.status === SelectedFileStatus.Uploading),
    [state.items],
  );

  const contextValue = useMemo<UploadQueueContextType>(
    () => ({
      items: state.items,
      onAddFiles: handleOnAddFiles,
      onRemoveItem: handleOnRemoveItem,
      onClearCompleted: handleOnClearCompleted,
      isUploading,
    }),
    [state.items, handleOnAddFiles, handleOnRemoveItem, handleOnClearCompleted, isUploading],
  );

  useEffect(() => {
    processQueue();
  }, [processQueue]);

  useEffect(() => {
    if (!isUploading) return;

    // Warn user before navigating away during uploads
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      return;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  useEffect(() => {
    // Cleanup timers on unmount
    return () => {
      for (const timer of autoRemoveTimersRef.current.values()) {
        clearTimeout(timer);
      }
      autoRemoveTimersRef.current.clear();
    };
  }, []);

  return (
    <UploadQueueContext.Provider value={contextValue}>
      {children}
      <UploadQueue />
    </UploadQueueContext.Provider>
  );
}
